import React from "react";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface SelectOption {
  label: string;
  value: string;
}

export interface SelectFieldProps {
  label?: string;
  description?: string;
  error?: string;
  required?: boolean;
  placeholder?: string;
  options: SelectOption[];
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  id?: string;
  style?: React.CSSProperties;
}

type SelectState = "default" | "filled" | "open" | "error" | "disabled";

// ─── Styles ───────────────────────────────────────────────────────────────────

const TEXT_STYLE: React.CSSProperties = {
  fontFamily: "var(--font-family-base)",
  fontSize: "var(--font-size-label-s)",
  fontWeight: "var(--font-weight-medium)",
  lineHeight: "var(--line-height-label-s)",
};

function getTriggerStyles(state: SelectState): React.CSSProperties {
  const base: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    width: "100%",
    height: 40,
    paddingInline: "var(--spacing-m)",
    borderRadius: "var(--radius-s)",
    border: "1px solid transparent",
    boxSizing: "border-box",
    transition: "border-color 120ms ease, background-color 120ms ease",
    cursor: "pointer",
    userSelect: "none",
  };
  switch (state) {
    case "open":
      return { ...base, background: "var(--color-bg-primary)", borderColor: "var(--color-border-brand)" };
    case "filled":
      return { ...base, background: "var(--color-bg-primary)", borderColor: "var(--color-border-primary)" };
    case "error":
      return { ...base, background: "var(--color-bg-primary)", borderColor: "var(--color-border-danger)" };
    case "disabled":
      return { ...base, background: "var(--color-bg-accent)", opacity: 0.6, cursor: "not-allowed" };
    default:
      return { ...base, background: "var(--color-bg-accent)" };
  }
}

// ─── Icons ────────────────────────────────────────────────────────────────────

const ChevronIcon = ({ open }: { open: boolean }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    style={{
      flexShrink: 0,
      transition: "transform 120ms ease",
      transform: open ? "rotate(180deg)" : "rotate(0deg)",
      color: "var(--color-content-secondary)",
    }}
  >
    <path d="M4 6l4 4 4-4" />
  </svg>
);

const WarningIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
    <path d="M8 2L14.5 13.5H1.5L8 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    <path d="M8 6.5V9.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <circle cx="8" cy="11.5" r="0.75" fill="currentColor" />
  </svg>
);

// ─── Component ────────────────────────────────────────────────────────────────

export function SelectField({
  label,
  description,
  error,
  required,
  placeholder = "Select an option",
  options,
  value,
  defaultValue,
  onChange,
  disabled,
  id,
  style,
}: SelectFieldProps) {
  const uidRef = React.useRef(`makula-select-${Math.random().toString(36).slice(2)}`);
  const triggerId = id ?? uidRef.current;

  const [open, setOpen] = React.useState(false);
  const [internalValue, setInternalValue] = React.useState(defaultValue ?? "");
  const containerRef = React.useRef<HTMLDivElement>(null);

  const isControlled = value !== undefined;
  const currentValue = isControlled ? value : internalValue;
  const selectedOption = options.find((o) => o.value === currentValue);

  const state: SelectState = disabled
    ? "disabled"
    : error
    ? "error"
    : open
    ? "open"
    : currentValue
    ? "filled"
    : "default";

  function handleSelect(optValue: string) {
    if (!isControlled) setInternalValue(optValue);
    onChange?.(optValue);
    setOpen(false);
  }

  // Close on outside click
  React.useEffect(() => {
    if (!open) return;
    function handleOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [open]);

  return (
    <div
      ref={containerRef}
      style={{ display: "flex", flexDirection: "column", gap: "var(--spacing-s)", width: "100%", position: "relative", ...style }}
    >
      {label && (
        <label
          htmlFor={triggerId}
          style={{
            ...TEXT_STYLE,
            display: "flex",
            gap: "var(--spacing-2xs)",
            alignItems: "center",
            color: "var(--color-content-secondary)",
            opacity: disabled ? 0.6 : 1,
            cursor: disabled ? "not-allowed" : "default",
          }}
        >
          <span>{label}</span>
          {required && <span aria-hidden="true">*</span>}
        </label>
      )}

      {/* Trigger */}
      <button
        id={triggerId}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => !disabled && setOpen((v) => !v)}
        style={{
          ...getTriggerStyles(state),
          gap: "var(--spacing-s)",
          outline: "none",
          appearance: "none",
          WebkitAppearance: "none",
        }}
      >
        <span
          style={{
            ...TEXT_STYLE,
            flex: 1,
            minWidth: 0,
            textAlign: "left",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            color: selectedOption
              ? "var(--color-content-primary)"
              : "var(--color-content-secondary)",
          }}
        >
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronIcon open={open} />
      </button>

      {/* Dropdown */}
      {open && (
        <ul
          role="listbox"
          style={{
            position: "absolute",
            top: "calc(100% + var(--spacing-xs))",
            left: 0,
            right: 0,
            background: "var(--color-bg-primary)",
            border: "1px solid var(--color-border-primary)",
            borderRadius: "var(--radius-s)",
            boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
            zIndex: 100,
            margin: 0,
            padding: "var(--spacing-2xs)",
            listStyle: "none",
            boxSizing: "border-box",
          }}
        >
          {options.map((opt) => {
            const isSelected = opt.value === currentValue;
            return (
              <li
                key={opt.value}
                role="option"
                aria-selected={isSelected}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSelect(opt.value);
                }}
                style={{
                  ...TEXT_STYLE,
                  padding: "var(--spacing-s) var(--spacing-m)",
                  borderRadius: "var(--radius-xs)",
                  cursor: "pointer",
                  color: isSelected ? "var(--color-content-brand)" : "var(--color-content-primary)",
                  background: isSelected ? "var(--color-bg-brand-lightest)" : "transparent",
                  transition: "background 80ms ease",
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) (e.currentTarget as HTMLElement).style.background = "var(--color-bg-accent)";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.background = isSelected ? "var(--color-bg-brand-lightest)" : "transparent";
                }}
              >
                {opt.label}
              </li>
            );
          })}
        </ul>
      )}

      {/* Error row */}
      {state === "error" && error && (
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: "var(--spacing-xs)",
            paddingInline: "var(--spacing-2xs)",
            color: "var(--color-content-danger)",
          }}
        >
          <WarningIcon />
          <span style={{ ...TEXT_STYLE, flex: 1, color: "var(--color-content-danger)" }}>{error}</span>
        </div>
      )}

      {/* Description */}
      {state !== "error" && description && (
        <span style={{ ...TEXT_STYLE, color: "var(--color-content-secondary)" }}>{description}</span>
      )}
    </div>
  );
}
